import {
  Navigate, RouterProvider, createBrowserRouter
} from 'react-router-dom';

import { EnvProvider } from '@zenbusiness/application-commons-env';
import PageError from '@zenbusiness/zui/PageError';
import PageLoading from '@zenbusiness/zui/PageLoading';

import Authenticated from '#client/App/Authenticated';
import BusinessEntityGate from '#client/App/BusinessEntityGate';
import ClickstreamWrapper from '#client/App/ClickstreamWrapper';
import CustomerRouteGuard from '#client/App/CustomerRouteGuard';
import CustomerRouter from '#client/App/CustomerRouter';
import PostRouting from '#client/App/PostRouting';
import { DEFAULT_SEGMENT } from '#client/build-env';
import GlobalComponents from '#client/components/GlobalComponents';
import Template from '#client/components/Template';
import {
  login, logout, callback, accountNotFound, mobileAccountNotFound, accountSettings, getStarted,
  userBlocked, loginError, invoicePaymentReturn, invoicePayment
} from '#client/constants/routes';
import * as Pages from '#client/pages';
import BillingLayout from '#client/pages/Billing';
import BusinessProfile from '#client/pages/BusinessProfile';
import { DEPLOY_ENV } from '#client/public-env';

import * as MFE from './MFEs';
import redirects from './Redirects';

const router = createBrowserRouter([
  ...redirects,
  {
    /**
     * Routes which do not require authentication at page load
     */
    element: (
      <ClickstreamWrapper>
        <PostRouting />
      </ClickstreamWrapper>
    ),
    children: [
      { index: true, element: <Pages.Landing /> },
      { path: login, element: <Pages.Login /> },
      { path: logout, element: <Pages.Logout /> },
      { path: callback, element: <Pages.Callback /> },
      { path: accountNotFound, element: <Pages.AccountNotFound /> },
      { path: mobileAccountNotFound, element: <Pages.AccountNotFoundMobile /> },
      { path: userBlocked, element: <Pages.UserBlocked /> },
      { path: loginError, element: <Pages.LoginError /> },
      { path: invoicePaymentReturn, element: <Pages.PaymentReturn /> },
      { path: invoicePayment, element: <Pages.InvoicePayments /> }
    ]
  },
  {
    /**
     * Routes which require authentication
     */
    path: DEFAULT_SEGMENT,
    element: (
      <Authenticated>
        <Template>
          <GlobalComponents>
            <PostRouting />
          </GlobalComponents>
        </Template>
      </Authenticated>
    ),
    children: [
      {
        path: 'account',
        children: [
          { path: 'payments', element: <Navigate to="/dashboard/subscription-hub/billing/methods" replace /> },
          { path: 'settings', element: <Pages.AccountSettings /> },
          { path: '*', element: <Navigate to={accountSettings} /> }
        ]
      },
      { path: 'companies', element: <Pages.BusinessOverview /> },
      { path: 'download', element: <Pages.Download /> },
      {
        path: 'subscription-hub/billing',
        element: <BillingLayout />,
        children: [
          { index: true, element: <Navigate to="methods" replace /> },
          { path: 'subscriptions', element: <Pages.Subscriptions /> },
          { path: 'methods', element: <MFE.EcommerceUi exportName="PaymentMethodsManagement" /> }
        ]
      },
      { path: 'financial-readiness', element: <MFE.MoneyUi /> },
      { path: 'help', element: <Pages.Help /> },
      { path: 'marketing/:redirectTo', element: <Pages.Landing /> },
      { index: true, element: <Pages.Landing /> }
    ]
  },
  {
    /**
     * Routes which can support authentication but do not require it
     */
    path: DEFAULT_SEGMENT,
    element: (
      <Authenticated optional>
        <PostRouting />
      </Authenticated>
    ),
    children: [
      { index: true, element: <Pages.Landing /> },
      { path: 'dashboard', element: <Pages.Landing /> },
      { path: getStarted, element: <Pages.OrphanAccountOptions /> }
    ]
  },
  {
    /**
     * Routes which require a business entity
     */
    path: `${DEFAULT_SEGMENT}/:businessEntityUuid`,
    element: (
      <Authenticated>
        <BusinessEntityGate>
          <CustomerRouteGuard>
            <Template>
              <GlobalComponents>
                <PostRouting />
              </GlobalComponents>
            </Template>
          </CustomerRouteGuard>
        </BusinessEntityGate>
      </Authenticated>
    ),
    children: [
      // change the index to home once ZenDash is federating
      { index: true, element: <CustomerRouter /> },
      { path: 'banking/*', element: <MFE.BankingUi /> },
      { path: 'business-profile', element: <BusinessProfile /> },
      { path: 'compliance/*', element: <MFE.ComplianceUi /> },
      { path: 'documents/*', element: <MFE.DocumentsUi /> },
      {
        path: 'education',
        element: <Pages.Education />,
        children: [
          { index: true, element: <Pages.EducationGettingStarted /> },
          { path: 'running-and-growing', element: <Pages.EducationRunningAndGrowing /> }
        ]
      },
      { path: 'guide/*', element: <MFE.AssistantUi /> },
      { path: 'invoicing/*', element: <MFE.InvoicingUi /> },
      { path: 'marketing-tools/*', element: <MFE.WebsitesUi /> },
      { path: 'money/*', element: <MFE.MoneyUi /> },
      { path: 'finances', element: <MFE.MoneyUi exportName="FinanceHub" /> },
      { path: 'finances/linked-accounts', element: <MFE.MoneyUi exportName="LinkedAccounts" /> },
      {
        path: 'subscription-hub',
        children: [
          { path: 'billing-history', element: <Pages.BillingHistory /> },
          { path: 'orders', element: <Pages.OrderHistory /> },
          { path: 'orders/:orderTaskUuid', element: <Pages.OrderView /> }
        ]
      },
      { path: 'worry-free-compliance', element: <Navigate to="products/wfc-services" replace /> },
      { path: 'products', element: <Pages.Products /> },
      { path: 'products/:productId', element: <Pages.ProductLanding /> },
      /* THIS IS FOR DEBUGGING PURPOSES ONLY */
      ...(DEPLOY_ENV !== 'production'
        ? [
          { path: 'x/*', element: <MFE.AppTemplateUi /> },
          { path: 'loading', element: <PageLoading /> }
        ]
        : [])
    ]
  },
  {
    /**
     * Full-screen routes that require a business entity but no dashboard chrome
     */
    path: `${DEFAULT_SEGMENT}/:businessEntityUuid/subscription-cancel/:orderBillingItemUuid`,
    element: (
      <Authenticated>
        <BusinessEntityGate>
          <PostRouting />
        </BusinessEntityGate>
      </Authenticated>
    ),
    children: [
      { index: true, element: <Pages.SubscriptionCancel /> }
    ]
  },
  {
    path: `${DEFAULT_SEGMENT}/:businessEntityUuid/subscription-restart/:orderBillingItemUuid`,
    element: (
      <Authenticated>
        <BusinessEntityGate>
          <PostRouting />
        </BusinessEntityGate>
      </Authenticated>
    ),
    children: [
      { index: true, element: <Pages.SubscriptionRestart /> }
    ]
  },
  {
    path: '*',
    element: (
      <ClickstreamWrapper>
        <PageError>
          The requested route could not be found.
        </PageError>
      </ClickstreamWrapper>
    )
  }
], {
  scrollRestoration: 'manual'
});

export function DirectRoutes() {
  return (
    // ROOT_SEGMENT is used to identify where the router is mounted at.
    // Since this is a direct entry router, the root is the MFE's DEFAULT_SEGMENT
    <EnvProvider env={{ ROOT_SEGMENT: DEFAULT_SEGMENT }}>
      <RouterProvider router={router} />
    </EnvProvider>
  );
}
